import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useFontSize } from '../contexts/FontContext';
import { useTranslation } from '../translations';
import { 
  MoonIcon, 
  SunIcon,
  GlobeAltIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const SettingsPage = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { language, changeLanguage } = useLanguage();
  const { fontSize, changeFontSize, getFontClass } = useFontSize();
  const { t } = useTranslation(language);

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 pt-4">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('settingsTitle')}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('settingsSubtitle')}
          </p>
        </div>

        {/* Settings Sections */}
        <div className="space-y-4">
          {/* Appearance Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className={`text-lg font-semibold text-gray-900 dark:text-white flex items-center`}>
                <SunIcon className="w-4 h-4 mr-2" />
                {t('appearance')}
              </h2>
              <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1`}>
                {t('appearanceDesc')}
              </p>
            </div>
            <div className="p-4">
              {/* Dark Mode Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {isDarkMode ? (
                    <MoonIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                  ) : (
                    <SunIcon className="w-4 h-4 text-yellow-600 mr-2" />
                  )}
                  <div>
                    <label className={`${getFontClass('text')} font-medium text-gray-900 dark:text-white`}>
                      {t('darkMode')}
                    </label>
                    <p className={`${getFontClass('text')} text-gray-600 dark:text-gray-400`}>
                      {isDarkMode ? t('darkModeEnabled') : t('lightModeEnabled')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={toggleTheme}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isDarkMode ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isDarkMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Language Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className={`text-lg font-semibold text-gray-900 dark:text-white flex items-center`}>
                <GlobeAltIcon className="w-4 h-4 mr-2" />
                {t('language')}
              </h2>
              <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1`}>
                {t('languageDesc')}
              </p>
            </div>
            <div className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => changeLanguage('tr')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    language === 'tr'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'
                  }`}
                >
                  <div className="text-center">
                    <div className={`${getFontClass('text')} font-medium text-gray-900 dark:text-white`}>
                      🇹🇷 {t('turkish')}
                    </div>
                    {language === 'tr' && (
                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                        Seçili
                      </div>
                    )}
                  </div>
                </button>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    language === 'en'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'
                  }`}
                >
                  <div className="text-center">
                    <div className={`${getFontClass('text')} font-medium text-gray-900 dark:text-white`}>
                      🇺🇸 {t('english')}
                    </div>
                    {language === 'en' && (
                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                        Selected
                      </div>
                    )}
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Font Size Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className={`text-lg font-semibold text-gray-900 dark:text-white flex items-center`}>
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t('fontSize')}
              </h2>
              <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1`}>
                {t('fontSizeDesc')}
              </p>
            </div>
            <div className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                {['small', 'medium', 'large'].map((size) => (
                  <button
                    key={size}
                    onClick={() => changeFontSize(size)}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                      fontSize === size
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className={`font-medium text-gray-900 dark:text-white ${
                        size === 'small' ? 'text-sm' : 
                        size === 'medium' ? 'text-base' : 'text-lg'
                      }`}>
                        Aa
                      </div>
                      <div className={`${getFontClass('text')} mt-1 text-gray-600 dark:text-gray-400`}>
                        {t(size)}
                      </div>
                      {fontSize === size && (
                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          {language === 'tr' ? 'Seçili' : 'Selected'}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SettingsPage;